import type { User } from "firebase/auth";

const BASE = import.meta.env.VITE_API_URL ?? "";

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, init);
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(
      (body as { error?: string }).error ?? `API ${res.status}: ${path}`,
      res.status,
      (body as { code?: string }).code
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiFetchWithToken<T>(
  path: string,
  token: string,
  init?: RequestInit
): Promise<T> {
  return apiFetch<T>(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(init?.headers as Record<string, string>),
    },
  });
}

/**
 * Admin API fetch with automatic 401 retry using force-refreshed token.
 * Never calls signOut() on error — errors propagate to UI.
 */
export async function apiFetchAdmin<T>(path: string, user: User, init?: RequestInit): Promise<T> {
  const token = await user.getIdToken(); // auto-cached, refreshes if expired
  try {
    return await apiFetchWithToken<T>(path, token, init);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      // Force-refresh the token and retry once
      const freshToken = await user.getIdToken(true);
      return await apiFetchWithToken<T>(path, freshToken, init);
    }
    throw err;
  }
}

async function multipartRequest<T>(
  path: string,
  token: string,
  body: FormData,
  method: "POST" | "PUT"
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { Authorization: `Bearer ${token}` },
    body,
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new ApiError(
      (data as { error?: string }).error ?? `Upload failed: ${res.status}`,
      res.status
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Admin multipart upload (images, PDFs) — does not set Content-Type so
 * the browser sets it with the correct boundary. Retries once on 401 with
 * a force-refreshed token, same contract as apiFetchAdmin.
 */
export async function apiFetchAdminMultipart<T>(
  path: string,
  user: User,
  body: FormData,
  method: "POST" | "PUT" = "POST"
): Promise<T> {
  const token = await user.getIdToken();
  try {
    return await multipartRequest<T>(path, token, body, method);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      const freshToken = await user.getIdToken(true);
      return await multipartRequest<T>(path, freshToken, body, method);
    }
    throw err;
  }
}
